import { Camera } from "@capacitor/camera";
import styled from "@emotion/styled";
import * as E from "fp-ts/Either";
import { pipe } from "fp-ts/lib/function";
import * as TE from "fp-ts/TaskEither";
import React from "react";
import { taskEither } from "../either";
import { usePermissionDeniedModal } from "../hooks/usePermissionDeniedModal";
import { STRINGS } from "../strings";
import { translate } from "../translate";
import { toBase64 } from "../utils/fileUtils";
import { getImageBase64, getPhotoFromCamera } from "../utils/imageUtils";
import { DOCUMENT_OUTLINE_ICON, GALLERY_ICON, SCAN_DOCUMENT_ICON } from "../utils/svgs_new";
import { AppBottomSheet } from "./Generic/AppBottomSheet";
import { AppIcon } from "./Generic/AppIcon";
import { AppList, AppListItem } from "./Generic/AppList";
import { AppOverlay } from "./Generic/AppOverlay";

type Status =
  | { type: "Idle" }
  | { type: "Processing" }
  | { type: "Success"; url: string; isDisabled: boolean }
  | { type: "Error"; error: string };

type ChildProps = Status & { open: () => void; isSelfie?: boolean };

interface Props {
  photo: string | undefined;
  isDisabled?: boolean;
  canUploadFromGallery?: boolean;
  canUploadDocument?: boolean;
  isSelfie?: boolean;
  children: (status: ChildProps) => React.ReactNode;
  onSelect: (images: string | undefined) => void;
  onClickDisabled: () => void;
}

const UploadPhoto = (props: Props) => {
  const [isOpen, setOpen] = React.useState(false);
  const [isProcessing, setProcessing] = React.useState(false);
  const [error, setError] = React.useState<string | undefined>();
  const { openModal: openPermissionDeniedModal } = usePermissionDeniedModal();

  const withSetPhoto = async (callback: () => Promise<string | undefined>) => {
    try {
      setOpen(false);
      setProcessing(true);

      const result = await callback();

      if (result !== undefined) {
        props.onSelect(result);
      }

      setProcessing(false);
    } catch (e) {
      setProcessing(false);

      if (!/user cancelled/i.test(e.message) && !/no images picked/i.test(e.message)) {
        setError(e.message);
        console.error(e);
      }
    }
  };

  const handleClickGallery = async () => {
    const { photos: photosPermission } = await Camera.checkPermissions();

    if (photosPermission === "denied") {
      setOpen(false);
      openPermissionDeniedModal("photos");
      return;
    }

    const pickResult = await taskEither(
      Camera.pickImages({
        width: 1440,
        height: 1440,
        limit: 1,
      })
    );

    if (pickResult.type === "Left") {
      console.log("Error picking images", pickResult.value);
      return;
    }

    const { photos } = pickResult.value;

    return withSetPhoto(() => getImageBase64(photos[0]));
  };

  const handleClickScan = async () => {
    const { camera: cameraPermission } = await Camera.checkPermissions();

    if (cameraPermission === "denied") {
      setOpen(false);
      openPermissionDeniedModal("camera");
      return;
    }

    const getBase64PhotoFromCamera = pipe(
      TE.tryCatch(getPhotoFromCamera, E.toError),
      TE.chain((photo) => TE.tryCatch(() => getImageBase64(photo), E.toError))
    );

    pipe(
      await getBase64PhotoFromCamera(),
      E.fold(
        ($error) => console.error(`Error getting photo from camera: ${$error}`),
        (photo) => withSetPhoto(() => Promise.resolve(photo))
      )
    );
  };

  const handleSelectFile = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;

    if (files === null || files.length === 0) {
      return;
    }

    const [file] = files;

    const result = await toBase64(file);

    if (result === null) {
      return;
    }

    const base64 =
      typeof result === "string" ? result : btoa(String.fromCharCode(...new Uint8Array(result)));

    withSetPhoto(() => Promise.resolve(base64));
  };

  const status = ((): Status => {
    if (isProcessing) {
      return { type: "Processing" };
    }

    if (error !== undefined) {
      return { type: "Error", error };
    }

    if (props.photo !== undefined) {
      return { type: "Success", url: props.photo, isDisabled: props.isDisabled === true };
    }

    return { type: "Idle" };
  })();

  const childProps: ChildProps = {
    ...status,
    isSelfie: props.isSelfie,
    open: () => {
      if (props.isDisabled === true) {
        return props.onClickDisabled();
      }

      setOpen(true);
    },
  };

  return (
    <>
      {props.children(childProps)}
      <AppOverlay>
        <UploadPhoto.Styled isOpen={isOpen} onRequestClose={() => setOpen(false)}>
          <AppList bordered={true}>
            <AppListItem
              leading={<AppIcon icon={SCAN_DOCUMENT_ICON} size={20} fill="var(--gray-600)" />}
              title={translate(
                props.isSelfie === true ? STRINGS.OPEN_CAMERA : STRINGS.SCAN_WITH_CAMERA
              )}
              onClick={handleClickScan}
            />

            {props.canUploadFromGallery === true ? (
              <AppListItem
                leading={<AppIcon icon={GALLERY_ICON} size={20} fill="var(--gray-600)" />}
                title={translate(STRINGS.UPLOAD_FROM_GALLERY)}
                onClick={handleClickGallery}
              />
            ) : null}

            {props.canUploadDocument !== false && props.isSelfie !== true ? (
              <>
                <label htmlFor="file-upload">
                  <AppListItem
                    leading={<AppIcon icon={DOCUMENT_OUTLINE_ICON} size={20} />}
                    title={translate(STRINGS.ATTACH_A_DOCUMENT)}
                  />
                </label>
                <input
                  id="file-upload"
                  type="file"
                  accept=".pdf"
                  style={{ display: "none" }}
                  onChange={handleSelectFile}
                />
              </>
            ) : null}
          </AppList>
        </UploadPhoto.Styled>
      </AppOverlay>
    </>
  );
};

UploadPhoto.Styled = styled(AppBottomSheet)`
  .AppBottomSheet_Content {
    margin: 0 !important;
  }

  .AppListItem {
    padding: 24px;
  }
`;

export default UploadPhoto;
